import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Clock, AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

/**
 * Componente que exibe o indicador de SLA para um ato de controle
 */
export const SLAIndicator = ({ actId, actType, controlBody, deadline, compact = false }) => {
  const { api } = useAuth();
  const [slaData, setSlaData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSLA = async () => {
      if (!actId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await api.get(`/sla/act/${actId}`);
        setSlaData(response.data);
      } catch (error) {
        console.error('Erro ao buscar SLA:', error);
        setSlaData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSLA();
  }, [actId, api]);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-6 w-24 bg-slate-200 rounded"></div>
      </div>
    );
  }

  if (!slaData || slaData.status === 'NO_DEADLINE' || slaData.status === 'ERROR') {
    return (
      <Badge variant="outline" className="text-slate-500">
        <Info className="w-3 h-3 mr-1" />
        Sem SLA
      </Badge>
    );
  }

  const getStatusIcon = () => {
    switch (slaData.status) {
      case 'ON_TIME':
        return <CheckCircle className="w-4 h-4" />;
      case 'WARNING':
        return <Clock className="w-4 h-4" />;
      case 'AT_RISK':
        return <AlertTriangle className="w-4 h-4" />;
      case 'BREACHED':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const getStatusStyles = () => {
    const baseStyles = "flex items-center gap-1.5 font-medium";
    switch (slaData.status) {
      case 'ON_TIME':
        return `${baseStyles} bg-emerald-50 text-emerald-700 border-emerald-200`;
      case 'WARNING':
        return `${baseStyles} bg-amber-50 text-amber-700 border-amber-200`;
      case 'AT_RISK':
        return `${baseStyles} bg-red-50 text-red-700 border-red-200`;
      case 'BREACHED':
        return `${baseStyles} bg-red-100 text-red-800 border-red-300`;
      default:
        return `${baseStyles} bg-slate-50 text-slate-600 border-slate-200`;
    }
  };

  const getProgressColor = () => {
    switch (slaData.status) {
      case 'ON_TIME':
        return 'bg-emerald-500';
      case 'WARNING':
        return 'bg-amber-500';
      case 'AT_RISK':
      case 'BREACHED':
        return 'bg-red-500';
      default:
        return 'bg-slate-400';
    }
  };

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className={getStatusStyles()}>
              {getStatusIcon()}
              {slaData.label}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-sm">
              <p><strong>SLA:</strong> {slaData.sla_days} dias</p>
              <p><strong>Status:</strong> {slaData.description}</p>
              {slaData.days_remaining !== undefined && (
                <p><strong>Dias restantes:</strong> {slaData.days_remaining}</p>
              )}
              {slaData.days_over && (
                <p className="text-red-600"><strong>Dias em atraso:</strong> {slaData.days_over}</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="bg-white rounded-lg border p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Indicador SLA</span>
          <Badge className={getStatusStyles()}>
            {getStatusIcon()}
            {slaData.label}
          </Badge>
        </div>
        <span className="text-xs text-slate-500">
          Meta: {slaData.sla_days} dias
        </span>
      </div>

      <div className="space-y-1">
        <div className="flex justify-between text-xs text-slate-500">
          <span>Progresso do prazo</span>
          <span>{Math.round(slaData.percentage || 0)}%</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all ${getProgressColor()}`}
            style={{ width: `${Math.min(100, Math.max(0, 100 - (slaData.percentage || 0)))}%` }}
          />
        </div>
      </div>

      <p className="text-xs text-slate-500">
        {slaData.description}
        {slaData.status === 'BREACHED' && slaData.days_over && (
          <span className="text-red-600 font-medium ml-1">
            ({slaData.days_over} dias em atraso)
          </span>
        )}
        {slaData.days_remaining !== undefined && slaData.days_remaining >= 0 && (
          <span className="ml-1">
            ({slaData.days_remaining} dias restantes)
          </span>
        )}
      </p>
    </div>
  );
};

/**
 * Card de resumo de SLA para dashboards
 */
export const SLASummaryCard = ({ className = '' }) => {
  const { api } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await api.get('/sla/summary');
        setSummary(response.data);
      } catch (error) {
        console.error('Erro ao buscar resumo SLA:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [api]);

  if (loading) {
    return (
      <div className={`animate-pulse bg-slate-100 rounded-xl h-48 ${className}`}></div>
    );
  }

  if (!summary) return null;

  const total = summary.total || 1;
  const segments = [
    { label: 'No Prazo', value: summary.on_time, color: 'bg-emerald-500' },
    { label: 'Atenção', value: summary.warning, color: 'bg-amber-500' },
    { label: 'Em Risco', value: summary.at_risk, color: 'bg-orange-500' },
    { label: 'Violado', value: summary.breached, color: 'bg-red-500' },
  ];

  return (
    <div className={`bg-white rounded-xl border p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-800">Indicadores de SLA</h3>
        <Badge variant="outline" className="text-sm">
          {summary.compliance_rate}% Conformidade
        </Badge>
      </div>

      {/* Barra de progresso segmentada */}
      <div className="flex h-3 rounded-full overflow-hidden mb-4">
        {segments.map((seg, idx) => (
          <div
            key={idx}
            className={`${seg.color} transition-all`}
            style={{ width: `${(seg.value / total) * 100}%` }}
            title={`${seg.label}: ${seg.value}`}
          />
        ))}
      </div>

      {/* Legenda */}
      <div className="grid grid-cols-2 gap-3">
        {segments.map((seg, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${seg.color}`}></div>
            <span className="text-sm text-slate-600">{seg.label}</span>
            <span className="text-sm font-semibold text-slate-800 ml-auto">{seg.value}</span>
          </div>
        ))}
      </div>

      {/* Métricas adicionais */}
      <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-3 text-center">
        <div>
          <p className="text-xl font-bold text-emerald-600" data-testid="sla-avg-days-deadline">{summary.avg_days_to_deadline}</p>
          <p className="text-xs text-slate-500">Dias médios no prazo</p>
        </div>
        <div>
          <p className="text-xl font-bold text-red-600" data-testid="sla-avg-days-overdue">{summary.avg_days_overdue || 0}</p>
          <p className="text-xs text-slate-500">Dias médios de atraso</p>
        </div>
        <div>
          <p className="text-xl font-bold text-slate-800" data-testid="sla-total-acts">{summary.total}</p>
          <p className="text-xs text-slate-500">Atos em andamento</p>
        </div>
      </div>
    </div>
  );
};

export default SLAIndicator;
